import { memo, useCallback, useEffect, useState } from 'react';
import { Button, Divider, Flex } from '@chakra-ui/react';
import { MdShowChart } from 'react-icons/md';
import { RiBarChartFill } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';

import {
  getChartFilter,
  getModelDetails,
  overallTimeFilter,
  setChartFilter,
} from 'redux/PlanPageSlice';
import {
  getCompareChartFilter,
  getCompareScenario,
  getVarianceFilterChart,
  setCompareChartFilter,
  toggleVarianceFilterChart,
} from 'redux/ScenarioSlice';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

import DownloadIcon from '../icons/DownloadIcon';
import { PLAN_KPI_SELECTED_BG, PLAN_TABLE_TOOLBAR_HEIGHT, PLAN_TEXT } from '../planPageRevampUi';

import PlanTimeFilterSegment, { PlanTimeFilterValue } from './PlanTimeFilterSegment';
import PlanToggleSquareButton from './PlanToggleSquareButton';

interface Properties {
  viewMode: string;
  onChange: (value: string) => void;
  onDownload?: () => void;
  isExporting?: boolean;
}

const PlanChartToolbar = ({ viewMode, onChange, onDownload, isExporting = false }: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const themeColors = useSelector(getPlanPageThemeColors);
  const modelDetail = useSelector(getModelDetails);
  const selectedYear = useSelector(overallTimeFilter);
  const planChartFilter = useSelector(getChartFilter);
  const compareChartFilter = useSelector(getCompareChartFilter);
  const varianceFilter = useSelector(getVarianceFilterChart);
  const compareScenario = useSelector(getCompareScenario);
  const activeChartFilter = (
    compareScenario ? compareChartFilter : planChartFilter
  ) as PlanTimeFilterValue;

  const timeAggregation = modelDetail?.time_properties?.time_granularity
    ? String(modelDetail.time_properties.time_granularity).toUpperCase()
    : 'M';

  const [displayLevels, setDisplayLevels] = useState<PlanTimeFilterValue>(activeChartFilter);

  useEffect(() => {
    setDisplayLevels(activeChartFilter);
  }, [activeChartFilter]);

  useEffect(() => {
    if (displayLevels === 'Y' && selectedYear !== 'all') {
      setDisplayLevels('M');
      if (compareScenario) {
        dispatch(setCompareChartFilter('M'));
      } else {
        dispatch(setChartFilter('M'));
      }
    }
  }, [compareScenario, dispatch, displayLevels, selectedYear]);

  const onChangeSummaryMode = useCallback(
    (value: PlanTimeFilterValue) => {
      setDisplayLevels(value);
      if (compareScenario) {
        dispatch(setCompareChartFilter(value));
        return;
      }
      dispatch(setChartFilter(value));
    },
    [compareScenario, dispatch],
  );

  const onToggleVariance = useCallback(() => {
    dispatch(toggleVarianceFilterChart());
  }, [dispatch]);

  return (
    <Flex
      w='100%'
      h={PLAN_TABLE_TOOLBAR_HEIGHT}
      bg={PLAN_KPI_SELECTED_BG}
      alignItems='stretch'
      justifyContent='flex-end'
      flexShrink={0}
      overflow='hidden'
      borderBottom='1px solid'
      borderColor={themeColors.border}
    >
      <Flex alignItems='stretch' gap={1} h='100%'>
        {compareScenario && (
          <>
            <Flex alignItems='center' px={4} h='100%'>
              <Button
                size='sm'
                h='28px'
                minH='28px'
                w='28px'
                minW='28px'
                p={0}
                {...PLAN_TEXT.labelSmMedium}
                borderRadius='8px'
                border='1px solid'
                borderColor={varianceFilter ? themeColors.primary : themeColors.border}
                bg={themeColors.surface}
                color={themeColors.primary}
                boxShadow='none'
                _hover={{ borderColor: themeColors.primary }}
                onClick={onToggleVariance}
              >
                %
              </Button>
            </Flex>
            <Divider
              orientation='vertical'
              h='20px'
              alignSelf='center'
              borderColor={themeColors.border}
            />
          </>
        )}

        {timeAggregation !== 'Y' && (
          <>
            <Flex alignItems='center' gap={3} px={4} h='100%'>
              <PlanTimeFilterSegment
                variant='tableTime'
                value={displayLevels}
                onChange={onChangeSummaryMode}
              />
            </Flex>
            <Divider
              orientation='vertical'
              h='20px'
              alignSelf='center'
              borderColor={themeColors.border}
            />
          </>
        )}

        <Flex alignItems='center' gap={3} px={4} h='100%'>
          <PlanToggleSquareButton
            variant='tableTime'
            isActive={viewMode === 'bar'}
            ariaLabel='Bar chart'
            icon={<RiBarChartFill size={12} />}
            onClick={() => onChange('bar')}
          />
          <PlanToggleSquareButton
            variant='tableTime'
            isActive={viewMode === 'line'}
            ariaLabel='Line chart'
            icon={<MdShowChart size={12} />}
            onClick={() => onChange('line')}
          />
        </Flex>

        {onDownload && (
          <Button
            h='100%'
            minH={PLAN_TABLE_TOOLBAR_HEIGHT}
            px={3}
            gap={2}
            borderRadius={0}
            {...PLAN_TEXT.bodyMedium}
            color={themeColors.white}
            bg={themeColors.primary}
            leftIcon={<DownloadIcon width={16} height={16} color={themeColors.white} />}
            _hover={{ bg: themeColors.primaryHover }}
            _active={{ bg: themeColors.primaryPressed }}
            flexShrink={0}
            isLoading={isExporting}
            width='fit-content'
            loadingText='Downloading'
            onClick={onDownload}
          >
            Download
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

export default memo(PlanChartToolbar);
